import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiTrendingUp, FiCalendar, FiAward } = FiIcons;

const Dashboard = ({ setCurrentView }) => {
  const [stats, setStats] = useState({
    totalWords: 0,
    dailyAverage: 0,
    thisWeekWords: 0,
    bestDay: 'No data yet',
    currentStreak: 0,
    totalDays: 0
  });

  useEffect(() => {
    calculateStats();
    
    // Add event listener for storage changes to update stats when data changes
    const handleStorageChange = () => {
      calculateStats();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for focus events to refresh when returning to dashboard
    window.addEventListener('focus', calculateStats);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', calculateStats);
    };
  }, []);

  const calculateStats = () => {
    // Use correct localStorage keys
    const dailyEntries = JSON.parse(localStorage.getItem('gospelwise-entries') || '[]');
    const projects = JSON.parse(localStorage.getItem('gospelwise-projects') || '[]');

    if (dailyEntries.length === 0 && projects.length === 0) {
      setStats({
        totalWords: 0,
        dailyAverage: 0,
        thisWeekWords: 0,
        bestDay: 'No data yet',
        currentStreak: 0,
        totalDays: 0
      });
      return;
    }

    // Calculate total words from both daily entries and projects
    const dailyTotal = dailyEntries.reduce((sum, entry) => sum + (entry.words || 0), 0);
    const projectTotal = projects.reduce((sum, project) => sum + (project.currentWords || 0), 0);
    const totalWords = dailyTotal + projectTotal;

    // Calculate this week's words (last 7 days)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    const thisWeekWords = dailyEntries
      .filter(entry => new Date(entry.date) >= oneWeekAgo)
      .reduce((sum, entry) => sum + (entry.words || 0), 0);

    // Find best day (day of week with highest word count)
    let bestDay = 'No data yet';
    if (dailyEntries.length > 0) {
      // Group entries by day of week
      const dayTotals = {};
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      dailyEntries.forEach(entry => {
        const date = new Date(entry.date);
        const dayOfWeek = dayNames[date.getDay()];
        dayTotals[dayOfWeek] = (dayTotals[dayOfWeek] || 0) + (entry.words || 0);
      });

      // Find the day with the highest total
      let maxWords = 0;
      let maxDay = '';
      
      Object.entries(dayTotals).forEach(([day, words]) => {
        if (words > maxWords) {
          maxWords = words;
          maxDay = day;
        }
      });

      bestDay = maxDay || 'No data yet';
    }

    // Calculate daily average from daily entries only (projects are long-term)
    const totalDays = Math.max(dailyEntries.length, 1);
    const dailyAverage = dailyEntries.length > 0 ? Math.round(dailyTotal / totalDays) : 0;

    setStats({
      totalWords,
      dailyAverage,
      thisWeekWords,
      bestDay,
      currentStreak: 0, // You can implement streak calculation later
      totalDays: dailyEntries.length
    });
  };

  // Add a function to manually refresh stats (called when navigating to dashboard)
  useEffect(() => {
    const refreshStats = () => {
      calculateStats();
    };
    
    // Set up an interval to refresh stats periodically
    const interval = setInterval(refreshStats, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const statCards = [
    {
      title: 'Total Words',
      value: stats.totalWords.toLocaleString(),
      icon: FiTarget,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      title: 'This Week',
      value: stats.thisWeekWords.toLocaleString(),
      icon: FiCalendar,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      title: 'Daily Average',
      value: stats.dailyAverage.toLocaleString(),
      icon: FiTrendingUp,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      title: 'Best Day',
      value: stats.bestDay,
      icon: FiAward,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg"
      >
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Welcome back, Writer!</h1>
        <p style={{
      fontWeight: '500',
      textAlign: 'left',
      fontSize: '15px'
    }} className="text-slate-600">For we are God's handiwork, created in Christ Jesus to do good works, which God prepared in advance for us to do. ~ Ephesians 2:10, NIV</p>
      </motion.div>

      {/* Stats Grid - 2x2 Layout */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6">
        {statCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/80 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 sm:p-3 rounded-xl ${card.bgColor}`}>
                <SafeIcon icon={card.icon} className={`text-lg sm:text-xl ${card.textColor}`} />
              </div>
            </div>
            <div>
              <h3 className="text-xs sm:text-sm font-medium text-slate-600 mb-1">{card.title}</h3>
              <p className="text-xl sm:text-2xl font-bold text-slate-800">{card.value}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg"
      >
        <h2 className="text-xl font-bold text-slate-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setCurrentView('daily')}
            className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 rounded-xl font-medium text-left hover:shadow-lg transition-all duration-300"
          >
            <SafeIcon icon={FiTarget} className="text-2xl mb-2" />
            <div>
              <h3 className="font-bold">Log Today's Writing</h3>
              <p className="text-sm opacity-90">Record your daily progress</p>
            </div>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setCurrentView('project')}
            className="bg-gradient-to-r from-purple-600 to-pink-700 text-white p-4 rounded-xl font-medium text-left hover:shadow-lg transition-all duration-300"
          >
            <SafeIcon icon={FiCalendar} className="text-2xl mb-2" />
            <div>
              <h3 className="font-bold">Manage Projects</h3>
              <p className="text-sm opacity-90">Track your writing goals</p>
            </div>
          </motion.button>
        </div>
      </motion.div>

      {/* Encouragement Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-100 shadow-lg"
      >
        <h2 className="text-xl font-bold text-slate-800 mb-3">Daily Encouragement</h2>
        <p className="text-slate-700 leading-relaxed">
          Remember, every word you write is a step toward the calling God has placed on your heart. 
          Whether you're crafting fiction that reflects His truth or non-fiction that teaches His ways, 
          your writing matters. Stay faithful to the process, and trust Him with the results.
        </p>
      </motion.div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="text-center mt-6"
      >
        <p className="text-xs text-slate-400">
          <a 
            href="https://www.gospelwiseauthor.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#ff8057] hover:text-[#e5714d] transition-colors font-medium"
          >
            Your writing journey starts here
          </a> • Made with ❤️ for Christian writers by CROSS + PEN
        </p>
      </motion.div>
    </div>
  );
};

export default Dashboard;